/**
 * BackfillWizard — reconstruct a stretch of untracked time, backwards.
 *
 * The quick-log flow covers "I just finished something"; this covers
 * "reconstruct my morning" without six manual dialogs. It walks BACKWARDS
 * from a boundary (usually the start of the most recent entry, or now) to an
 * anchor (usually the start of the earliest entry today), one block at a
 * time, most recent first — because memory works backwards from the last
 * thing you did.
 *
 * Unaccounted time is derived by subtracting the day's tracked intervals
 * from [anchor, boundary]. The server's GET /api/time-entries/gaps endpoint
 * is the source for surfacing between-entry gaps (setup summary and the
 * auto-offer on the Time Entries page); the subtraction here additionally
 * covers the day edges, which that endpoint deliberately does not invent.
 *
 * State is a list of SEGMENTS with stable ids (see utils/backfillTimeline),
 * not the old blocks-plus-decisions stack whose cursor *was* the decision
 * count. That change is what makes random access possible: any segment can be
 * selected and re-edited at any time, and its edges dragged on the timeline
 * with roll/ripple propagation. The guided backwards walk still drives the
 * cursor by default — it is what makes six hours tractable — and clicking a
 * segment simply takes manual control.
 *
 * Because a single ripple drag can move many segments at once, undo is a
 * stack of whole-timeline snapshots rather than "pop one decision".
 *
 * The whole proposed set is committed atomically via
 * POST /api/time-entries/bulk — one transaction, client-minted batch id as
 * the double-submit guard (a 409 means "already saved", not an error).
 *
 * Keyboard-first: number keys pick from the shortlist, Enter accepts,
 * S skips, M merges into the previous (more recent) block, Backspace steps
 * back, R flips roll/ripple for the next gesture, Up/Down move the selection.
 * Someone reconstructing six hours multiplies every extra click.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import axios from 'axios';

import BackfillTimeline, { DragPhase } from './BackfillTimeline';
import { useBackfillTimelinePreferences } from '../hooks/useBackfillTimelinePreferences';
import { getApiErrorMessage } from '../utils/apiError';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import {
  BlockFields,
  Edge,
  EditMode,
  Interval,
  MIN_BLOCK_MS,
  Segment,
  buildSegments,
  canMerge as canMergeSegments,
  durationMs,
  emptyFields,
  findInternalOverlaps,
  magnetsFrom,
  mergeSegments,
  proposedSegments,
  resizeSegment,
  splitSegment,
  unaccountedWindows,
  walkOrder,
} from '../utils/backfillTimeline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface BackfillGap {
  start: string;
  end: string;
  minutes?: number;
}

interface WizardEntry {
  id: number;
  description?: string | null;
  project?: string | null;
  task?: string | null;
  tags?: string | null;
  start_time: string;
  end_time?: string | null;
}

/** One overlap warning attached to a committed row, mirroring the server shape. */
export interface CommittedOverlap {
  id: number;
  start_time: string;
  end_time?: string | null;
  description?: string | null;
  project?: string | null;
  task?: string | null;
  acknowledged?: boolean;
}

export interface CommittedEntry {
  id: number;
  start_time: string;
  end_time?: string | null;
  description?: string | null;
  overlaps?: CommittedOverlap[];
}

interface ReviewEntry {
  start: string; // datetime-local input value
  end: string;
  description: string;
  project: string;
  task: string;
  tags: string;
}

interface BackfillWizardProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called after a successful (or already-committed) bulk commit. `created`
   * carries the persisted rows and their overlap warnings so the page — which
   * already owns an OverlapConflictDialog mount — can surface conflicts
   * without this dialog growing a second conflict UI.
   */
  onCommitted: (count: number, created?: CommittedEntry[]) => void;
  /** Pre-seed anchor/boundary — used by the auto-offer on the page. */
  initialGap?: BackfillGap | null;
}

type Step = 'setup' | 'fill' | 'review';

interface Snapshot {
  segments: Segment[];
  selectedId: string | null;
  fields: BlockFields;
  lastAccepted: BlockFields | null;
  step: Step;
}

const BLOCK_PRESETS = [15, 30, 45, 60];
const SHORTLIST_SIZE = 5;
// How far back to look for prefill/shortlist candidates.
const LOOKBACK_DAYS = 7;
/** Breathing room either side of the proposal on the timeline. */
const VIEW_PADDING_MS = 30 * 60000;

const toLocalInputValue = (date: Date): string => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toLocalDateValue = (date: Date): string => toLocalInputValue(date).slice(0, 10);

const fmtTime = (d: Date): string =>
  d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const fmtMinutes = (minutes: number): string => {
  const whole = Math.max(0, Math.round(minutes));
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const optional = (value: string): string | null => value.trim() || null;

// Batch id charset must satisfy the server's ^[A-Za-z0-9-]{8,64}$ contract.
const genBatchId = (): string => {
  const cryptoObj =
    typeof crypto !== 'undefined'
      ? (crypto as Crypto & { randomUUID?: () => string })
      : undefined;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  return `bf${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

const entryFields = (entry: WizardEntry): BlockFields => ({
  description: entry.description || '',
  project: entry.project || '',
  task: entry.task || '',
  tags: entry.tags || '',
});

/** Closed entries as plain intervals, for window subtraction and the timeline. */
const closedIntervals = (entries: WizardEntry[]): Interval[] =>
  entries
    .filter((entry) => entry.end_time)
    .map((entry) => ({
      start: new Date(entry.start_time),
      end: new Date(entry.end_time as string),
    }))
    .filter((iv) => !Number.isNaN(iv.start.getTime()) && !Number.isNaN(iv.end.getTime()));

const BackfillWizard: React.FC<BackfillWizardProps> = ({
  open,
  onClose,
  onCommitted,
  initialGap = null,
}) => {
  const { settings: timelinePrefs } = useBackfillTimelinePreferences();
  const { dialogContentPaddingSx } = useLayoutTokens();
  const [step, setStep] = useState<Step>('setup');
  // Starts true so "Start walking" can't fire before the first load lands.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentEntries, setRecentEntries] = useState<WizardEntry[]>([]);
  const [dayGaps, setDayGaps] = useState<BackfillGap[]>([]);
  const [anchor, setAnchor] = useState('');
  const [boundary, setBoundary] = useState('');
  const [blockPreset, setBlockPreset] = useState<number>(30);
  const [customMinutes, setCustomMinutes] = useState('');
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [fields, setFields] = useState<BlockFields>(emptyFields);
  const [lastAccepted, setLastAccepted] = useState<BlockFields | null>(null);
  // Only ever read inside its own updater — undo pops the top and applies it,
  // so the stack never needs to drive a render.
  const [, setUndoStack] = useState<Snapshot[]>([]);
  const [modeOverride, setModeOverride] = useState<EditMode | null>(null);
  const [review, setReview] = useState<ReviewEntry[]>([]);
  const [committing, setCommitting] = useState(false);
  const batchIdRef = useRef<string>(genBatchId());
  // The timeline as it stood when the current drag began. Every move applies
  // from *this*, so dragging to a time is idempotent rather than compounding.
  const dragBaseRef = useRef<Segment[] | null>(null);

  const order = useMemo(() => walkOrder(segments), [segments]);
  const currentIndex = selectedId ? order.indexOf(selectedId) : -1;
  const currentSegment = segments.find((segment) => segment.id === selectedId) || null;

  /** The mode this gesture will use: the account default unless R flipped it. */
  const effectiveMode: EditMode = modeOverride ?? timelinePrefs.edit_mode;

  const tracked = useMemo(() => closedIntervals(recentEntries), [recentEntries]);

  // Most-frequently-used shortlist from the recent entries.
  const shortlist = useMemo(() => {
    const groups = new Map<string, { fields: BlockFields; count: number; latest: number }>();
    for (const entry of recentEntries) {
      if (!entry.end_time) continue;
      const f = entryFields(entry);
      const key = [f.description, f.project, f.task].join('\0');
      if (!f.description && !f.project && !f.task) continue;
      const started = new Date(entry.start_time).getTime();
      const existing = groups.get(key);
      if (existing) {
        existing.count += 1;
        existing.latest = Math.max(existing.latest, started);
      } else {
        groups.set(key, { fields: f, count: 1, latest: started });
      }
    }
    return Array.from(groups.values())
      .sort((a, b) => b.count - a.count || b.latest - a.latest)
      .slice(0, SHORTLIST_SIZE);
  }, [recentEntries]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const now = new Date();
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart.getTime() + 24 * 3600000);
      const lookbackStart = new Date(dayStart.getTime() - LOOKBACK_DAYS * 24 * 3600000);
      const [entriesRes, gapsRes] = await Promise.all([
        axios.get<WizardEntry[]>(`${API_BASE_URL}/api/time-entries/`, {
          params: {
            from: lookbackStart.toISOString(),
            to: dayEnd.toISOString(),
            limit: 500,
          },
        }),
        axios.get<{ gaps: BackfillGap[] }>(`${API_BASE_URL}/api/time-entries/gaps`, {
          params: { date: toLocalDateValue(now), tz_offset: now.getTimezoneOffset() },
        }),
      ]);
      const entries = entriesRes.data || [];
      setRecentEntries(entries);
      setDayGaps(Array.isArray(gapsRes.data?.gaps) ? gapsRes.data.gaps : []);

      if (initialGap) {
        setAnchor(toLocalInputValue(new Date(initialGap.start)));
        setBoundary(toLocalInputValue(new Date(initialGap.end)));
      } else {
        const today = entries.filter((e) => {
          if (!e.end_time) return false;
          const started = new Date(e.start_time);
          return started >= dayStart && started < dayEnd;
        });
        const starts = today.map((e) => new Date(e.start_time).getTime());
        const defaultAnchor = starts.length
          ? new Date(Math.min(...starts))
          : (() => {
              const nine = new Date(dayStart);
              nine.setHours(9, 0, 0, 0);
              return nine;
            })();
        const defaultBoundary = starts.length ? new Date(Math.max(...starts)) : now;
        setAnchor(toLocalInputValue(defaultAnchor));
        setBoundary(toLocalInputValue(defaultBoundary));
      }
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load today\'s entries'));
    } finally {
      setLoading(false);
    }
  }, [initialGap]);

  useEffect(() => {
    if (!open) return;
    batchIdRef.current = genBatchId();
    setStep('setup');
    setSegments([]);
    setSelectedId(null);
    setFields(emptyFields);
    setLastAccepted(null);
    setUndoStack([]);
    setModeOverride(null);
    setReview([]);
    setError(null);
    loadData();
  }, [open, loadData]);

  const prefillFrom = useCallback(
    (accepted: BlockFields | null): BlockFields => {
      if (accepted) return accepted;
      const nearest = recentEntries.find((entry) => entry.end_time);
      return nearest ? entryFields(nearest) : emptyFields;
    },
    [recentEntries],
  );

  const totalUnaccountedMinutes = useMemo(
    () =>
      dayGaps.reduce(
        (sum, gap) =>
          sum +
          (gap.minutes ??
            Math.round(
              (new Date(gap.end).getTime() - new Date(gap.start).getTime()) / 60000,
            )),
        0,
      ),
    [dayGaps],
  );

  /**
   * Hard bounds for a drag: never into the future, never across the local day
   * boundary (the /gaps endpoint is day-scoped, so anything rippled past
   * midnight would land on a day this UI never shows). The setup anchor is
   * deliberately NOT a bound — realising at 09:30 that the morning started at
   * 07:00 must not require restarting the wizard.
   */
  const resizeOptions = useMemo(() => {
    const now = new Date();
    const dayStart = new Date(now);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart.getTime() + 24 * 3600000);
    return {
      minDurationMs: MIN_BLOCK_MS,
      lowerBound: dayStart,
      upperBound: new Date(Math.min(now.getTime(), dayEnd.getTime())),
    };
  }, []);

  const viewRange = useMemo(() => {
    if (segments.length === 0) {
      const now = new Date();
      return { start: new Date(now.getTime() - 3600000), end: now };
    }
    const earliest = Math.min(...segments.map((s) => s.start.getTime()));
    const latest = Math.max(...segments.map((s) => s.end.getTime()));
    const relevant = tracked.filter(
      (iv) => iv.end.getTime() > earliest - VIEW_PADDING_MS && iv.start.getTime() < latest + VIEW_PADDING_MS,
    );
    const start = Math.min(earliest, ...relevant.map((iv) => iv.start.getTime()));
    const end = Math.max(latest, ...relevant.map((iv) => iv.end.getTime()));
    return {
      start: new Date(start - VIEW_PADDING_MS),
      end: new Date(end + VIEW_PADDING_MS),
    };
  }, [segments, tracked]);

  const magnets = useMemo(() => magnetsFrom(tracked), [tracked]);

  const snapshot = useCallback(
    (): Snapshot => ({ segments, selectedId, fields, lastAccepted, step }),
    [segments, selectedId, fields, lastAccepted, step],
  );

  const pushUndo = useCallback(() => {
    setUndoStack((prev) => [...prev, snapshot()]);
  }, [snapshot]);

  /**
   * Next segment still awaiting a decision, resuming after `afterId` in walk
   * order and wrapping once. Wrapping matters now that the owner can jump back
   * to an earlier block: finishing it must return to whatever is still pending,
   * not fall through to review.
   */
  const nextPendingId = useCallback(
    (list: Segment[], afterId: string | null): string | null => {
      const ids = walkOrder(list);
      const byId = new Map(list.map((segment) => [segment.id, segment]));
      const from = afterId ? ids.indexOf(afterId) + 1 : 0;
      const rotated = [...ids.slice(from), ...ids.slice(0, Math.max(0, from))];
      return rotated.find((id) => byId.get(id)?.state === 'pending') ?? null;
    },
    [],
  );

  const enterReview = useCallback((list: Segment[]) => {
    setReview(
      proposedSegments(list).map((segment) => ({
        start: toLocalInputValue(segment.start),
        end: toLocalInputValue(segment.end),
        ...segment.fields,
      })),
    );
    setStep('review');
  }, []);

  /** Apply a decision, then hand the cursor to the next pending segment. */
  const advance = useCallback(
    (list: Segment[], decidedId: string | null, accepted: BlockFields | null) => {
      setSegments(list);
      if (accepted) setLastAccepted(accepted);
      const nextId = nextPendingId(list, decidedId);
      if (!nextId) {
        setSelectedId(null);
        enterReview(list);
        return;
      }
      setSelectedId(nextId);
      setFields(prefillFrom(accepted ?? lastAccepted));
    },
    [enterReview, lastAccepted, nextPendingId, prefillFrom],
  );

  const startWalk = () => {
    const anchorDate = new Date(anchor);
    const boundaryDate = new Date(boundary);
    if (Number.isNaN(anchorDate.getTime()) || Number.isNaN(boundaryDate.getTime())) {
      setError('Anchor and boundary must be valid times');
      return;
    }
    if (boundaryDate.getTime() <= anchorDate.getTime()) {
      setError('The boundary must be after the anchor');
      return;
    }
    const custom = Number(customMinutes.trim());
    const size = customMinutes.trim() && Number.isFinite(custom) && custom > 0
      ? custom
      : blockPreset;
    const windows = unaccountedWindows(anchorDate, boundaryDate, tracked);
    const nextSegments = buildSegments(windows, size);
    if (nextSegments.length === 0) {
      setError('Nothing unaccounted between the anchor and the boundary.');
      return;
    }
    setError(null);
    setSegments(nextSegments);
    setUndoStack([]);
    const firstId = walkOrder(nextSegments)[0] ?? null;
    setSelectedId(firstId);
    setFields(prefillFrom(null));
    setStep('fill');
  };

  const handleAccept = useCallback(() => {
    if (!currentSegment) return;
    pushUndo();
    const next = segments.map((segment) =>
      segment.id === currentSegment.id
        ? { ...segment, state: 'filled' as const, fields: { ...fields } }
        : segment,
    );
    advance(next, currentSegment.id, { ...fields });
  }, [advance, currentSegment, fields, pushUndo, segments]);

  const handleSameAsPrevious = useCallback(() => {
    if (!lastAccepted) return;
    setFields(lastAccepted);
  }, [lastAccepted]);

  const handleSkip = useCallback(() => {
    if (!currentSegment) return;
    pushUndo();
    const next = segments.map((segment) =>
      segment.id === currentSegment.id
        ? { ...segment, state: 'skipped' as const }
        : segment,
    );
    advance(next, currentSegment.id, null);
  }, [advance, currentSegment, pushUndo, segments]);

  // "Previous" in the walk is the *more recent* neighbour — the one already
  // decided — so a merge extends that entry backwards over this block.
  const mergeAllowed = selectedId ? canMergeSegments(segments, selectedId, 'later') : false;

  const handleMerge = useCallback(() => {
    if (!selectedId || !mergeAllowed) return;
    pushUndo();
    const next = mergeSegments(segments, selectedId, 'later');
    advance(next, null, null);
  }, [advance, mergeAllowed, pushUndo, segments, selectedId]);

  const handleSplit = useCallback(() => {
    if (!currentSegment) return;
    if (durationMs(currentSegment) < 2 * MIN_BLOCK_MS) return;
    pushUndo();
    const originalEnd = currentSegment.end.getTime();
    const next = splitSegment(segments, currentSegment.id);
    setSegments(next);
    // Keep the cursor on the newer half, so the walk continues where it was.
    const newer = next.find(
      (segment) => segment.end.getTime() === originalEnd && segment.id !== currentSegment.id,
    );
    if (newer) setSelectedId(newer.id);
  }, [currentSegment, pushUndo, segments]);

  const handleBack = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) {
        if (step === 'fill') setStep('setup');
        return prev;
      }
      const restored = prev[prev.length - 1];
      setSegments(restored.segments);
      setSelectedId(restored.selectedId);
      setFields(restored.fields);
      setLastAccepted(restored.lastAccepted);
      setStep(restored.step);
      return prev.slice(0, -1);
    });
  }, [step]);

  const pickShortlist = useCallback(
    (index: number) => {
      const option = shortlist[index];
      if (option) setFields(option.fields);
    },
    [shortlist],
  );

  const moveSelection = useCallback(
    (delta: number) => {
      if (order.length === 0) return;
      const from = currentIndex === -1 ? 0 : currentIndex;
      const nextIndex = Math.min(order.length - 1, Math.max(0, from + delta));
      const nextId = order[nextIndex];
      if (!nextId || nextId === selectedId) return;
      setSelectedId(nextId);
      const target = segments.find((segment) => segment.id === nextId);
      setFields(target && target.state === 'filled' ? target.fields : prefillFrom(lastAccepted));
    },
    [currentIndex, lastAccepted, order, prefillFrom, segments, selectedId],
  );

  const handleSelect = useCallback(
    (id: string) => {
      setSelectedId(id);
      const target = segments.find((segment) => segment.id === id);
      setFields(target && target.state === 'filled' ? target.fields : prefillFrom(lastAccepted));
    },
    [lastAccepted, prefillFrom, segments],
  );

  const handleEdgeDrag = useCallback(
    (id: string, edge: Edge, time: Date, phase: DragPhase) => {
      if (phase === 'start') {
        dragBaseRef.current = segments;
        pushUndo();
        return;
      }
      if (phase === 'end') {
        dragBaseRef.current = null;
        // A per-gesture override is spent once used: the mode belongs to the
        // gesture, never to the block.
        setModeOverride(null);
        return;
      }
      const base = dragBaseRef.current;
      if (!base) return;
      setSegments(resizeSegment(base, id, edge, time, effectiveMode, resizeOptions));
    },
    [effectiveMode, pushUndo, resizeOptions, segments],
  );

  /**
   * Walk shortcuts, scoped to the dialog rather than the window.
   *
   * Two hazards this has to dodge. Enter is left alone while a <button> has
   * focus so the browser's native activation fires instead of a double accept.
   * And anything inside the timeline is the timeline's own business — it
   * claims the arrow keys for edge nudging and stops those from bubbling, but
   * a focused drag handle must not have digits or S/M/Backspace stolen from
   * under it either.
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (step !== 'fill') return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName || '';
      const inField = tag === 'INPUT' || tag === 'TEXTAREA';
      if (event.key === 'Enter' && tag !== 'BUTTON') {
        event.preventDefault();
        handleAccept();
        return;
      }
      if (inField) return;
      if (target?.closest('[data-backfill-timeline]')) return;
      // Never hijack a browser or OS chord (Cmd+S, Ctrl+M, …).
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (/^[1-9]$/.test(event.key)) {
        pickShortlist(Number(event.key) - 1);
        return;
      }
      if (event.key === 's' || event.key === 'S') {
        handleSkip();
        return;
      }
      if (event.key === 'm' || event.key === 'M') {
        handleMerge();
        return;
      }
      if (event.key === 'r' || event.key === 'R') {
        event.preventDefault();
        setModeOverride((prev) =>
          prev ? null : timelinePrefs.edit_mode === 'ripple' ? 'roll' : 'ripple',
        );
        return;
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        moveSelection(event.key === 'ArrowUp' ? -1 : 1);
        return;
      }
      if (event.key === 'Backspace') {
        event.preventDefault();
        handleBack();
      }
    },
    [
      handleAccept,
      handleBack,
      handleMerge,
      handleSkip,
      moveSelection,
      pickShortlist,
      step,
      timelinePrefs.edit_mode,
    ],
  );

  const projectTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const entry of review) {
      const span =
        (new Date(entry.end).getTime() - new Date(entry.start).getTime()) / 60000;
      if (!Number.isFinite(span) || span <= 0) continue;
      const key = entry.project.trim() || '(No project)';
      totals.set(key, (totals.get(key) || 0) + span);
    }
    return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
  }, [review]);

  const updateReview = (index: number, patch: Partial<ReviewEntry>) => {
    setReview((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)),
    );
  };

  const handleCommit = async () => {
    try {
      setCommitting(true);
      const parsed = review.map((entry, index) => {
        const start = new Date(entry.start);
        const end = new Date(entry.end);
        if (
          Number.isNaN(start.getTime()) ||
          Number.isNaN(end.getTime()) ||
          end.getTime() <= start.getTime()
        ) {
          throw new Error(`Row ${index + 1}: start must be before end`);
        }
        return { entry, start, end, index };
      });
      // Dragging preserves the non-overlapping invariant; the review step's
      // free-text time fields do not, so check before the batch goes out.
      const selfOverlaps = findInternalOverlaps(
        parsed.map((row) => ({
          id: String(row.index),
          start: row.start,
          end: row.end,
          state: 'filled' as const,
          fields: emptyFields,
        })),
      );
      if (selfOverlaps.length > 0) {
        const [a, b] = selfOverlaps[0];
        throw new Error(
          `Rows ${Number(a.id) + 1} and ${Number(b.id) + 1} overlap each other. Adjust one of them before saving.`,
        );
      }
      const entries = parsed.map((row) => ({
        description: optional(row.entry.description),
        project: optional(row.entry.project),
        task: optional(row.entry.task),
        tags: optional(row.entry.tags),
        start_time: row.start.toISOString(),
        end_time: row.end.toISOString(),
      }));
      const response = await axios.post(`${API_BASE_URL}/api/time-entries/bulk`, {
        batch_id: batchIdRef.current,
        entries,
      });
      const created: CommittedEntry[] = Array.isArray(response.data?.created)
        ? response.data.created
        : [];
      onCommitted(response.data?.total ?? entries.length, created);
      onClose();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        // The batch already landed (double-submit / retried timeout).
        onCommitted(0);
        onClose();
        return;
      }
      const rowErrors = err?.response?.data?.detail?.errors;
      if (Array.isArray(rowErrors) && rowErrors.length > 0) {
        setError(
          rowErrors
            .map((row: any) => `Row ${Number(row?.index) + 1}: ${row?.error}`)
            .join(' · '),
        );
      } else if (!err?.response && err instanceof Error) {
        setError(err.message);
      } else {
        setError(getApiErrorMessage(err, 'Failed to save backfilled entries'));
      }
    } finally {
      setCommitting(false);
    }
  };

  const setField = (key: keyof BlockFields) =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setFields((prev) => ({ ...prev, [key]: event.target.value }));

  const blockMinutes = currentSegment ? Math.round(durationMs(currentSegment) / 60000) : 0;
  const walkCursorId = nextPendingId(segments, null);

  return (
    <Dialog
      open={open}
      onClose={() => (committing ? undefined : onClose())}
      onKeyDown={handleKeyDown}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>Backfill time entries</DialogTitle>
      <DialogContent sx={dialogContentPaddingSx}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 'setup' && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Walk backwards from the boundary to the anchor, one block at a
              time, and rebuild what you were doing. Only unaccounted time is
              offered — tracked entries are left alone. Block size is just a
              starting grid; every edge is draggable once the walk begins.
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              dayGaps.length > 0 && (
                <Alert severity="info">
                  {dayGaps.length === 1
                    ? `1 unaccounted gap today (${fmtMinutes(totalUnaccountedMinutes)}).`
                    : `${dayGaps.length} unaccounted gaps today (${fmtMinutes(totalUnaccountedMinutes)} total).`}
                </Alert>
              )
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Anchor (walk back to)"
                  InputLabelProps={{ shrink: true }}
                  value={anchor}
                  onChange={(e) => setAnchor(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Boundary (walk back from)"
                  InputLabelProps={{ shrink: true }}
                  value={boundary}
                  onChange={(e) => setBoundary(e.target.value)}
                />
              </Grid>
            </Grid>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Block size
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={customMinutes.trim() ? null : blockPreset}
                  onChange={(_, value) => {
                    if (value != null) {
                      setBlockPreset(value);
                      setCustomMinutes('');
                    }
                  }}
                  aria-label="Block size"
                >
                  {BLOCK_PRESETS.map((minutes) => (
                    <ToggleButton key={minutes} value={minutes}>
                      {minutes}m
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  type="number"
                  label="Custom minutes"
                  sx={{ width: 140 }}
                  inputProps={{ min: 5 }}
                  value={customMinutes}
                  onChange={(e) => setCustomMinutes(e.target.value)}
                />
              </Stack>
            </Box>
          </Stack>
        )}

        {step === 'fill' && currentSegment && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {timelinePrefs.show_timeline && (
              <BackfillTimeline
                segments={segments}
                tracked={tracked}
                viewStart={viewRange.start}
                viewEnd={viewRange.end}
                selectedId={selectedId}
                currentId={walkCursorId}
                editMode={effectiveMode}
                snapMinutes={timelinePrefs.snap_minutes}
                magnets={magnets}
                onSelect={handleSelect}
                onEdgeDrag={handleEdgeDrag}
              />
            )}

            <Box>
              <Typography variant="overline" color="text.secondary">
                Block {currentIndex + 1} of {segments.length}
              </Typography>
              <Typography variant="h6" data-testid="current-block-range">
                {fmtTime(currentSegment.start)} – {fmtTime(currentSegment.end)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {blockMinutes}m · walking backwards, most recent first
                {modeOverride ? ` · ${modeOverride} for the next drag (R)` : ''}
              </Typography>
            </Box>

            {shortlist.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Frequent (press the number)
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {shortlist.map((option, index) => (
                    <Chip
                      key={`${option.fields.description}-${option.fields.project}-${index}`}
                      label={`${index + 1}. ${option.fields.description || '(no description)'}${
                        option.fields.project ? ` · ${option.fields.project}` : ''
                      }`}
                      onClick={() => pickShortlist(index)}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={fields.description}
                  onChange={setField('description')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Project"
                  value={fields.project}
                  onChange={setField('project')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Task"
                  value={fields.task}
                  onChange={setField('task')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tags"
                  value={fields.tags}
                  onChange={setField('tags')}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" onClick={handleAccept}>
                Accept (Enter)
              </Button>
              <Button onClick={handleSameAsPrevious} disabled={!lastAccepted}>
                Same as previous
              </Button>
              <Button onClick={handleSkip}>Skip (S)</Button>
              <Button onClick={handleMerge} disabled={!mergeAllowed}>
                Merge into previous (M)
              </Button>
              <Button
                onClick={handleSplit}
                disabled={durationMs(currentSegment) < 2 * MIN_BLOCK_MS}
              >
                Split
              </Button>
              <Button onClick={handleBack}>Back (⌫)</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Skip marks a break. Merge extends the previous (more recent)
              block backwards so four blocks become one entry. Drag a block&apos;s
              edge to change when it started or ended — R flips roll/ripple for
              one drag, ↑/↓ move between blocks.
            </Typography>
          </Stack>
        )}

        {step === 'review' && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="subtitle1">
              Review {review.length} proposed {review.length === 1 ? 'entry' : 'entries'}
            </Typography>
            {review.length === 0 ? (
              <Alert severity="info">
                Every block was skipped — nothing to commit.
              </Alert>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  data-testid="project-totals"
                >
                  {projectTotals.map(([project, minutes]) => (
                    <Chip key={project} label={`${project} · ${fmtMinutes(minutes)}`} />
                  ))}
                </Stack>
                <Divider />
                <Stack spacing={2}>
                  {review.map((entry, index) => (
                    <Grid container spacing={1} key={`${entry.start}-${index}`}>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="datetime-local"
                          label="Start"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ 'aria-label': `Row ${index + 1} start` }}
                          value={entry.start}
                          onChange={(e) => updateReview(index, { start: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="datetime-local"
                          label="End"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ 'aria-label': `Row ${index + 1} end` }}
                          value={entry.end}
                          onChange={(e) => updateReview(index, { end: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Description"
                          inputProps={{ 'aria-label': `Row ${index + 1} description` }}
                          value={entry.description}
                          onChange={(e) =>
                            updateReview(index, { description: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid item xs={6} sm={1.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Project"
                          inputProps={{ 'aria-label': `Row ${index + 1} project` }}
                          value={entry.project}
                          onChange={(e) => updateReview(index, { project: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={6} sm={1.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Tags"
                          inputProps={{ 'aria-label': `Row ${index + 1} tags` }}
                          value={entry.tags}
                          onChange={(e) => updateReview(index, { tags: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Everything commits in one transaction — if any row is
                  invalid, nothing is saved.
                </Typography>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step !== 'setup' && (
          <Button onClick={handleBack} disabled={committing}>
            Back
          </Button>
        )}
        <Button onClick={onClose} disabled={committing}>
          Cancel
        </Button>
        {step === 'setup' && (
          <Button variant="contained" onClick={startWalk} disabled={loading}>
            Start walking
          </Button>
        )}
        {step === 'review' && review.length > 0 && (
          <Button variant="contained" onClick={handleCommit} disabled={committing}>
            {committing ? (
              <CircularProgress size={20} />
            ) : (
              `Commit ${review.length} ${review.length === 1 ? 'entry' : 'entries'}`
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BackfillWizard;
